<script setup lang="ts">
import { pb } from "@/utils/PocketBaseAdapter";
import { VForm } from "vuetify/components";
import type { BookingStepProps } from "../types/index";

const props = defineProps<
  Omit<BookingStepProps, "areaDurationDisable"> & {
    areaDurationDisable?: boolean;
  }
>();

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "next-step"): void;
}>();

const showActivityDetails = ref(false);
const selectedActivity = ref<any>(null);

const handleActivitySelect = (activityId: string) => {
  if(props.form.activity != activityId){
    props.form.time = null
    props.form.area = null
  }
  emit("update:form", { ...props.form, activity: activityId });
  emit("next-step");
};

const handleInfoClick = (event: Event, activity: any) => {
  event.stopPropagation(); // Prevent button click event
  selectedActivity.value = activity;
  showActivityDetails.value = true;
};

defineExpose({
  validate: async () => {
    return !!props.form.activity;
  },
});

const truncateDescription = (description: string) => {
  const maxLen = 50;

  return description.length > maxLen
    ? `${description.slice(0, maxLen)}...`
    : description;
};
</script>

<template>
  <VForm ref="activityFormRef">
    <!-- No activities message -->
    <VRow v-if="!activityList?.length">
      <VCol cols="12">
        <v-icon icon="mdi-alert-circle" />
        There are no activities available to book at this time.
      </VCol>
    </VRow>
    <VRow v-else>
      <VCol
        v-for="activity in activityList.sort((a:any,b:any) => a?.sortOrder == b?.sortOrder ? 0 : a?.sortOrder < b?.sortOrder ? -1 : 1)"
        :key="activity.id"
        cols="12"
        sm="6"
        md="4"
      >
        <VBtn
          block
          size="large"
          height="120"
          :color="activity.color"
          :class="{ selected: form.activity === activity.id }"
          class="d-flex activity-btn align-start position-relative text-white"
          @click="handleActivitySelect(activity.id)"
        >
          <VIcon
            icon="mdi-information-outline"
            class="position-absolute"
            style="top: 8px; right: 8px;"
            @click="handleInfoClick($event, activity)"
          />
          <div
            class="d-flex flex-column align-start text-left w-100 text-white"
          >
            <div class="text-h6 mb-2 text-white">
              {{ activity.name }}
            </div>
            <div class="text-body-2 text-wrap text-white">
              {{ truncateDescription(activity.description) }}
            </div>
          </div>
        </VBtn>
      </VCol>
    </VRow>
  </VForm>

  <!-- Activity Details Dialog -->
  <v-dialog v-model="showActivityDetails" :max-width="650">
    <VCard class="mx-auto overflow-visible" max-width="650">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <!-- Activity Image -->
            <div class="image-container">
              <img
                v-if="selectedActivity?.image"
                :src="`${pb.baseUrl}/api/files/${selectedActivity.collectionId}/${selectedActivity.id}/${selectedActivity.image}?token=`"
                alt="Activity Image"
                class="activity-image"
              />
            </div>
          </VCol>
        </VRow>
        <VRow>
          <VCol lg="4" md="4" cols="12">
            <VCol>
              <strong>Name:</strong>
              <div>{{ selectedActivity?.name }}</div>
            </VCol>
            <VCol>
              <strong>Normal Price:</strong>
              <div v-if="selectedActivity?.sessionFullPrice">
                ${{ selectedActivity.sessionFullPrice }} Per
                {{
                  selectedActivity.bookingSpacing - selectedActivity.bookingGap
                }}
                Min Session
              </div>
            </VCol>
          </VCol>
          <VCol md="8" cols="12">
            <VCol
              v-if="
                selectedActivity?.longDescription ||
                selectedActivity?.description
              "
            >
              <strong>Description:</strong>
              <div
                v-html="
                  selectedActivity?.longDescription ||
                  selectedActivity?.description
                "
              ></div>
            </VCol>
          </VCol>
        </VRow>
      </VCardText>

      <VCardActions class="d-flex justify-end">
        <VBtn color="error" variant="flat" @click="showActivityDetails = false">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </v-dialog>
</template>

<style lang="scss" scoped>
.activity-btn {
  padding: 16px;
  block-size: auto !important;
  min-block-size: 120px;
  white-space: normal;

  &.selected {
    border: 2px solid currentcolor;
  }

  &:not(.selected) {
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }

  :deep(.v-btn__content) {
    block-size: 100%;
    inline-size: 100%;
    white-space: normal;
  }
}

.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 4px;
  background-color: #f5f5f5;
  inline-size: 100%;
  min-block-size: 200px; // Minimum height for small content
}

.activity-image {
  block-size: auto;
  inline-size: 100%;
  object-fit: contain;
}
</style>
